import type { InviteEntryData } from "./InviteEntry";

export interface ValidationOptions {
  requireId?: boolean;
}

/**
 * Validates a single invite entry
 * @returns Error message if invalid, null if valid
 */
export function validateInviteEntry(
  entry: InviteEntryData,
  options: ValidationOptions = {},
): string | null {
  const { requireId = true } = options;

  if (!entry.firstName.trim()) {
    return "First name is required";
  }
  if (!entry.lastName.trim()) {
    return "Last name is required";
  }
  if (!entry.email.trim()) {
    return "Email is required";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry.email)) {
    return "Invalid email format";
  }
  if (requireId && !entry.customerId.trim()) {
    return "Customer ID is required";
  }
  // File is optional - only validate format if provided
  if (
    entry.customerHistoryFile &&
    !entry.customerHistoryFile.name.toLowerCase().endsWith(".csv")
  ) {
    return "Customer history must be a CSV file";
  }
  return null;
}

/**
 * Validates all entries and returns successful and failed entries
 */
export function validateAllEntries(
  entries: InviteEntryData[],
  options: ValidationOptions = {},
): {
  successful: InviteEntryData[];
  failed: Array<{ entry: InviteEntryData; error: string }>;
  errors: Record<number, string>;
} {
  const successful: InviteEntryData[] = [];
  const failed: Array<{ entry: InviteEntryData; error: string }> = [];
  const errors: Record<number, string> = {};

  entries.forEach((entry, index) => {
    const error = validateInviteEntry(entry, options);
    if (error) {
      failed.push({ entry, error });
      errors[index] = error;
    } else {
      successful.push(entry);
    }
  });

  return { successful, failed, errors };
}
